import React, { useEffect, useState } from "react";
import "../../App.css";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { FormLabel } from "../../components/form-label/FormLabel";
import axios from "axios";
import style from "./LeadCollect.module.css";
import {
  endpoint,
  nomeCognomeRegex,
  phoneRegex,
  provincieItaliane,
  required_error,
} from "../../services/shared";
import { ProfessioneEnum, Rules } from "../../types";
import {collection, onSnapshot} from "@firebase/firestore";
import { db } from "../../services/firebase";
import {useAuth} from "../../providers/AuthProvider";

const baseSchema = z.object({
  nome: z
      .string({ required_error })
      .min(2, "Il nome è obbligatorio")
      .regex(nomeCognomeRegex, "Il nome può contenere solo lettere e spazi"),
  cognome: z
      .string({ required_error })
      .min(2, "Il cognome è obbligatorio")
      .regex(nomeCognomeRegex, "Il cognome può contenere solo lettere e spazi"),
  cellulare: z
      .string({ required_error })
      .min(8, "Cellulare non valido")
      .regex(phoneRegex, "Numero di cellulare italiano non valido")
});
const advancedSchema = z.object({
  tipologiaRichiesta: z.enum(["Cessione del Quinto", "Mutuo Immobile"], {
    errorMap: () => ({ message: "Seleziona tipologia" }),
  }),
  distributionChannel: z.enum(["hub", "rete"], {
    errorMap: () => ({ message: "Seleziona destinazione" }),
  }),
  nome: z
    .string({ required_error })
    .min(2, "Il nome è obbligatorio")
    .regex(nomeCognomeRegex, "Il nome può contenere solo lettere e spazi"),
  cognome: z
    .string({ required_error })
    .min(2, "Il cognome è obbligatorio")
    .regex(nomeCognomeRegex, "Il cognome può contenere solo lettere e spazi"),
  importo: z.coerce.number().positive("Importo non valido").optional(),
  provincia: z.string({ required_error }).min(1, "Provincia obbligatoria"),
  email: z.string().email("Email non valida").optional(),
  cellulare: z
    .string({ required_error })
    .min(8, "Cellulare non valido")
    .regex(phoneRegex, "Numero di cellulare italiano non valido"),
  nomeAtc: z.string().optional(),
  pIva: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{11}$/.test(val), {
      message: "La Partita IVA deve contenere esattamente 11 cifre",
    }),
  rating: z.string().optional(),
  professione: z.nativeEnum(ProfessioneEnum, {
    errorMap: () => ({ message: "Seleziona una professione" }),
  }),
  sesso: z
    .enum(["F", "M"], {
      errorMap: () => ({ message: "Seleziona un sesso" }),
    })
    .optional(),
  dataNascita: z
    .string()
    .optional()
    .refine((val) => !val || /^\d{2}\/\d{2}\/\d{4}$/.test(val), {
      message: "Formato data non valido",
    }),
  note: z.string({ required_error }).min(10, "Inserisci una nota valida"),
});

const schema = z.discriminatedUnion("mode", [
  baseSchema.extend({ mode: z.literal("base") }),
  advancedSchema.extend({ mode: z.literal("advanced") }),
]);

type FormData = z.infer<typeof schema>;

function LeadCollect() {
  const {user, token, freshToken, customClaims} = useAuth();
  const [snackState, setSnackState] = useState<boolean>(false);
  const [mode, setMode] = useState<"base" | "advanced">("base");
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });

  const labels = {
    infoSmistamento: "Informazioni di smistamento",
    infoGenerali: "Informazioni generali",
    infoReddito: "Informazioni reddituali",
  };
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: mode === "advanced" ? {
      mode: "advanced",
      distributionChannel: undefined,
      tipologiaRichiesta: undefined,
      nome: "",
      cognome: "",
      importo: undefined,
      provincia: "",
      email: "",
      cellulare: "",
      nomeAtc: "",
      pIva: "",
      rating: "",
      professione: "" as any,
      sesso: undefined,
      dataNascita: "",
      note: "",
    } : {
      mode: "base",
      nome: "",
      cognome: "",
      cellulare: "",
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const [listaRegole, setListRegole] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const onSubmit = handleSubmit(async (data) => {
    setSnackState(false);
    if (mode === "advanced") {
      const regoleAttiveCanale = listaRegole.filter(
          (rule) =>
              rule.active && rule.distribution_channel === (data as any).distributionChannel,
      );

      if (regoleAttiveCanale.length > 0) {
        // Se almeno una ha accept_out_preference = false, allora verifica la provincia
        const almenoUnaConRestrizione = regoleAttiveCanale.some(
            (r) => r.accept_out_preference === false,
        );

        if (almenoUnaConRestrizione) {
          const provinciaAccettata = regoleAttiveCanale.some((r) =>
              r.province_preferences?.includes((data as any).provincia),
          );

          if (!provinciaAccettata) {
            setSnackMessage({
              severity: "error",
              message: "Provincia non prevista per questo canale",
            });
            setSnackState(true);
            return; // 🔴 interrompe il submit
          }
        }
      }
    }

    let res = null;
    try {
      const freshTK = await freshToken();
      // console.log({ user });
      res = await axios.post(
        `${endpoint}/legacy/v1/appPreq/lead_collect_handler_v2`,
        { ...data, uidSender: user?.uid },
        {
          headers: {
            "Authorization": `Bearer ${freshTK}`,
          },
        },
      );
    } catch (e) {
      console.error("errore", e);
      setSnackMessage({
        severity: "error",
        message: "Qualcosa è andato storto",
      });
      setSnackState(true);
    }
    if (res?.status && res?.status === 200) {
      setSnackMessage({ severity: "success", message: "Lead qualificato" });
      setSnackState(true);
      reset();
      setImportoDisplay("");
    }
  });

  const [importoDisplay, setImportoDisplay] = useState("");

  const formatToCurrency = (val: string) => {
    const cleaned = val.replace(/\D/g, "");
    if (!cleaned) return "";
    const number = parseFloat(cleaned) / 100;
    return number.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
// modifico lo schema del form in base alle caratteristiche utente
  useEffect(() => {
    if (!customClaims) return;
    // user è autorizzato alla creazione lead e non al quality
    if (customClaims.grants.includes("create_lead") && !customClaims.grants.includes("quality_op")) {
      if (mode !== "base") {
        setMode("base");
      }
    }
    // user è autorizzato alla creazione lead e anche al quality
    if (customClaims.grants.includes("create_lead") && customClaims.grants.includes("quality_op") || customClaims.grants.includes("user_admin") || customClaims.grants.includes("fornitore_admin")) {
      if (mode !== "advanced") {
        setMode("base");
      }
    }
  }, []);

// carico il set di regole per lo smist
  useEffect(() => {
    const colRef = collection(db, "rules");
    const unsubscribe = onSnapshot(
      colRef,
      (snap) => {
        const results: Rules[] = snap.docs.map((doc) => {
          return { ...doc.data(), uid: doc.id };
        }) as Rules[];
        setListRegole(results);
        setLoading(false); // ✅ qui!
      },
      (err) => {
        console.error("Errore nella snapshot:", err);
        setSnackMessage({ severity: "error", message: "Errore nel caricamento delle regole. Ricarica la pagina." });
        setSnackState(true);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);

  return (
    <div className="container">
      <div className={style.leadCollectContainer}>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={snackState}
          onClose={() => setSnackState(false)}
          autoHideDuration={3000}
        >
          <Alert
            onClose={() => setSnackState(false)}
            severity={snackMessage.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackMessage.message}
          </Alert>
        </Snackbar>
        <h2 style={{ textAlign: "start" }}>
          {mode !== "base" ? '📌 Inserisci nuovo LEAD' : '📣 Inoltra SMS raccolta privacy'}
        </h2>
        <form
          onSubmit={onSubmit}
          style={{
            border: "1px solid #d1d1d1",
            borderRadius: "0.5em",
            padding: "2em",
            margin: "auto",
            marginBottom: "1em",
            background: "white",
            boxShadow: "2px 2px 10px 2px rgba(100,100,100,0.1)",
          }}
          className="flex-col gap-2"
        >
          {mode === "advanced" && (
            <>
              <FormLabel label={labels.infoSmistamento}></FormLabel>
              <div className="flex-row gap-2">
                <Controller
                  name="tipologiaRichiesta"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%", maxWidth: "195px" }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="tipologia-label">
                          Tipologia richiesta
                        </InputLabel>
                        <Select
                          {...field}
                          style={{ textAlign: "left" }}
                          labelId="tipologia-label"
                          value={field.value || ""}
                          error={!!(errors as any).tipologiaRichiesta}
                        >
                          <MenuItem value="" disabled>
                            Seleziona una opzione
                          </MenuItem>
                          <MenuItem value="Cessione del Quinto">
                            Cessione
                          </MenuItem>
                          <MenuItem value="Mutuo Immobile">Mutuo</MenuItem>
                        </Select>
                        {(errors as any).tipologiaRichiesta && (
                          <FormHelperText style={{ color: "#d32f2f" }}>
                            {(errors as any).tipologiaRichiesta.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </div>
                  )}
                />
                <Controller
                  name="distributionChannel"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%", maxWidth: "195px" }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="canale-smist">
                          Canale destinazione
                        </InputLabel>
                        <Select
                          {...field}
                          style={{ textAlign: "left" }}
                          labelId="canale-smist"
                          value={field.value || ""}
                          error={!!(errors as any).distributionChannel}
                        >
                          <MenuItem value="" disabled>
                            Seleziona una opzione
                          </MenuItem>
                          <MenuItem value="hub">HUB</MenuItem>
                          <MenuItem value="rete">Rete</MenuItem>
                        </Select>
                        {(errors as any).distributionChannel && (
                          <FormHelperText style={{ color: "#d32f2f" }}>
                            {(errors as any).distributionChannel.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </div>
                  )}
                />
              </div>
              <FormLabel label={labels.infoGenerali}></FormLabel>
            </>
          )}
          <div className="flex-row gap-2">
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Nome"
                  placeholder="Es. Massimo"
                  value={field.value ?? ""}
                  error={!!errors.nome}
                  helperText={errors.nome?.message}
                />
              )}
            />
            <Controller
              name="cognome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Cognome"
                  placeholder="Es. Rossi"
                  value={field.value ?? ""}
                  error={!!errors.cognome}
                  helperText={errors.cognome?.message}
                />
              )}
            />
          </div>
          {mode === "advanced" && (
            <div className="flex-row gap-2">
              <Controller
                name="importo"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="Importo"
                    placeholder="Es. 10.000,00"
                    value={importoDisplay}
                    onChange={(e) => {
                      const input = e.target.value;
                      const cleaned = input.replace(/\D/g, "");
                      const number = parseFloat(cleaned) / 100;
                      setImportoDisplay(formatToCurrency(input));
                      field.onChange(isNaN(number) ? undefined : number);
                    }}
                    error={!!(errors as any).importo}
                    helperText={(errors as any).importo?.message}
                  />
                )}
              />
              <Controller
                name="provincia"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={provincieItaliane}
                    sx={{ width: 300 }}
                    value={field.value || null}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Provincia"
                        error={!!(errors as any).provincia}
                        helperText={(errors as any).provincia?.message}
                      />
                    )}
                  />
                )}
              />
            </div>
          )}
          {mode === "advanced" && (
            <div className="flex-row gap-2">
              <Controller
                name="sesso"
                control={control}
                render={({ field }) => (
                  <div style={{ width: "50%", maxWidth: "195px" }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="sesso-label">Seleziona sesso</InputLabel>
                      <Select
                        {...field}
                        style={{ textAlign: "left" }}
                        labelId="sesso-label"
                        value={field.value || ""}
                        error={!!(errors as any).sesso}
                      >
                        <MenuItem value="" disabled>
                          Seleziona una opzione
                        </MenuItem>
                        <MenuItem value="F">Donna</MenuItem>
                        <MenuItem value="M">Uomo</MenuItem>
                      </Select>
                      {(errors as any).sesso && (
                        <FormHelperText style={{ color: "#d32f2f" }}>
                          {(errors as any).sesso.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>
                )}
              />
              <Controller
                name="dataNascita"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label="Data di nascita"
                    placeholder="Es. 11/08/1987"
                    value={field.value ?? ""}
                    error={!!(errors as any).dataNascita}
                    helperText={(errors as any).dataNascita?.message}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // rimuove tutto tranne numeri
                      if (value.length >= 3 && value.length <= 4)
                        value = value.replace(/^(\d{2})(\d{1,2})/, "$1/$2");
                      else if (value.length > 4)
                        value = value.replace(
                          /^(\d{2})(\d{2})(\d{1,4}).*/,
                          "$1/$2/$3",
                        );

                      field.onChange(value);
                    }}
                  />
                )}
              />
            </div>
          )}
          <div className="flex-row gap-2">
            <Controller
              name="cellulare"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Cellulare"
                  value={field.value || ""}
                  placeholder="Inserisci cellulare"
                  error={!!errors.cellulare}
                  helperText={errors.cellulare?.message}
                />
              )}
            />
            {
              mode === "advanced" &&
                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Email"
                            value={field.value || ""}
                            placeholder="Inserisci email"
                            error={!!(errors as any).email}
                            helperText={(errors as any).email?.message}
                        />
                    )}
                />
            }
          </div>
          {
            mode === "advanced" &&
              <>
                <FormLabel label={labels.infoReddito}></FormLabel>
                <div className="flex-row gap-2">
                  <Controller
                      name="nomeAtc"
                      control={control}
                      render={({ field }) => (
                          <TextField
                              {...field}
                              size="small"
                              label="Nome ATC"
                              placeholder="Inserisci nome ATC"
                              value={field.value || ""}
                              error={!!(errors as any).nomeAtc}
                              helperText={(errors as any).nomeAtc?.message}
                          />
                      )}
                  />
                  <Controller
                      name="pIva"
                      control={control}
                      render={({ field }) => (
                          <TextField
                              {...field}
                              size="small"
                              label="Partita IVA"
                              placeholder="Inserisci P.IVA"
                              value={field.value || ""}
                              error={!!(errors as any).pIva}
                              helperText={(errors as any).pIva?.message}
                          />
                      )}
                  />
                </div>
                <div className="flex-row gap-2">
                  <Controller
                      name="rating"
                      control={control}
                      render={({ field }) => (
                          <TextField
                              {...field}
                              size="small"
                              label="Valutazione ATC"
                              placeholder="Inserisci valutazione"
                              value={field.value || ""}
                              error={!!(errors as any).rating}
                              helperText={(errors as any).rating?.message}
                          />
                      )}
                  />
                  <Controller
                      name="professione"
                      control={control}
                      render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel id="professione-label">
                              Seleziona professione
                            </InputLabel>
                            <Select
                                {...field}
                                style={{ textAlign: "left" }}
                                labelId="professione-label"
                                value={field.value || ""}
                                error={!!(errors as any).professione}
                            >
                              <MenuItem value="" disabled>
                                Seleziona una opzione
                              </MenuItem>
                              {Object.entries(ProfessioneEnum).map(([key, value]) => (
                                  <MenuItem key={key} value={value}>
                                    {value}
                                  </MenuItem>
                              ))}
                            </Select>
                            {(errors as any).professione && (
                                <FormHelperText style={{ color: "#d32f2f" }}>
                                  {(errors as any).professione.message}
                                </FormHelperText>
                            )}
                          </FormControl>
                      )}
                  />
                </div>
                <Controller
                    name="note"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Note"
                            multiline
                            rows={4}
                            placeholder="Scrivi qualcosa..."
                            fullWidth
                            error={!!(errors as any).note}
                            helperText={(errors as any).note?.message}
                        />
                    )}
                />
              </>
          }
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Elaboro richiesta..." : "Invia"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default LeadCollect;
